import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Canvas } from '@react-three/fiber';
import Scene from '../../structural/scene/scene'; 
import SettingsMenu from '../../structural/screen/settingsMenu';
import SponsortsBar from '../../structural/screen/sponsorsBar';
import Controls from '../../structural//controls/cameraControls';
import { PerspectiveCamera } from '@react-three/drei';

import 
{ 
  createSpacingMatrix, 
  createGridMatrix,
  calculatePivot,
  createHeightMatrix,
  createNodesMatrix,
  createColumnsMatrix,
  createBeamsMatrix,
  calculateTotalVolume,
  calculateTotalWeight,
  createSlabsMatrix,
  structuralCalculations,
  calculateArea,
  createReactionsMatrix
} from '../../structural/helpers';

const Main = () => 
  {
    const [xAxis, setXAxis] = useState(3); // Eje X por defecto de 2
    const [zAxis, setZAxis] = useState(3); // Eje Y por defecto de 2
    const [floors, setFloors] = useState(2); // Número de pisos
    const [selectedFloor, setSelectedFloor] = useState(1);
    const [intersectionSpacing, setIntersectionSpacing] = useState(1); // Espacio entre intersecciones (por defecto 1 metro)
  
    const [spacingMatrix, setSpacingMatrix] = useState([]);
    const [heightMatrix, setHeightMatrix] = useState([]);
    const [totalHeight, setTotalHeight] = useState([]);
  
    const [gridPoints, setGridPoints] = useState([]);
    const [pivotPoint, setPivotPoint] = useState({ x: 0, y: 0, z: 0 });
  
    const [nodes, setNodes] = useState({ x: 0, y: 0, z: 0 });
  
    const [columnsMatrix, setColumnMatrix] = useState([]);
    const [beamsMatrix, setBeamsMatrix] = useState([]);
    const [slabsMatrix, setSlabsMatrix] = useState([]);
  
    const [totalVolume, setTotalVolume] = useState();
    const [totalWeight, setTotalWeight] = useState();
    const [totalArea, setTotalArea] = useState();
  
    const [selectedSpacing, setSelectedSpacing] = useState('A1 - B1');
    const [selectedAxes, setSelectedAxesSpacing] = useState(["A","B", "Z"] );
  
    const [structuralCalculus, setSetStructuralCalculus] = useState();
  
    const densityConcrete = 2.4;

     // Actualizar la matriz de altura
     useEffect(() => 
    {
      const prevMatrix  = []
      let matrix1 = createHeightMatrix(floors, prevMatrix);
  
      setHeightMatrix(matrix1);
      setTotalHeight(Number(matrix1.slice(0, floors).reduce((acc, curr) => acc + parseFloat(curr.height), 0)).toFixed(2));
      
    }, [floors]);
  
    // Actualizar la matriz cuando cambien los ejes, sin perder valores previos
    useEffect(() => 
    {
      setSpacingMatrix((prevMatrix) => createSpacingMatrix(xAxis, zAxis, prevMatrix));
    }, [xAxis, zAxis]);
  
    //Se crea la Matrix de los ejes
    useEffect(() => 
    {
      //console.time("GRID");
      setGridPoints(createGridMatrix(spacingMatrix, xAxis, zAxis));
      setNodes(createNodesMatrix(spacingMatrix, xAxis, zAxis, heightMatrix)); 
      //console.timeEnd("GRID");
   
    }, [spacingMatrix, xAxis, zAxis, heightMatrix, floors]);
   
    //Cálculo del Pivot
    useEffect(() => 
    {
      //console.time("PIVOT");
      if (gridPoints.length === 0) return;
  
      // Calcular pivot con la nueva matriz de puntos
      const pivot = calculatePivot(gridPoints, totalHeight/2, pivotPoint);
      setPivotPoint(pivot);
      //console.timeEnd("PIVOT");
  
    }, [gridPoints, totalHeight ]);
      
    // Generar la matriz y publicarla en la consola cuando cambien los ejes
    useEffect(() => 
    {
      console.time("MATRICES");

      //console.time("MATRIZ COLUMNAS");
      let matrix1 = createColumnsMatrix(gridPoints, heightMatrix); 
      //console.timeEnd("MATRIZ COLUMNAS");

      //console.time("MATRIZ VIGAS");
      let matrix2 = createBeamsMatrix(gridPoints, heightMatrix, matrix1);
      //console.timeEnd("MATRIZ VIGAS");
      
      //console.time("MATRIZ LOSAS");
      let matrix3 = createSlabsMatrix(xAxis, zAxis, gridPoints, heightMatrix, matrix2, selectedAxes, selectedFloor);    
      //console.timeEnd("MATRIZ LOSAS");

      setColumnMatrix(matrix1);
      setBeamsMatrix(matrix2);
      setSlabsMatrix(matrix3);
  
      //console.time("VOLUMEN - PESO - AREA");
      setTotalVolume(() => calculateTotalVolume(matrix1, matrix2, matrix3));
      setTotalWeight(() => calculateTotalWeight(matrix1, matrix2, matrix3));
      setTotalArea(() => calculateArea(spacingMatrix, xAxis, zAxis, floors));      
      //console.timeEnd("VOLUMEN - PESO - AREA");      

      console.timeEnd("MATRICES");

    }, [xAxis, zAxis, floors, gridPoints, heightMatrix]); 
  
    //Cálculos Estructurales  
    useEffect(() => 
    {
      console.time("CÁLCULOS ESTRUCTURALES");
      setSetStructuralCalculus(structuralCalculations(slabsMatrix));
      console.timeEnd("CÁLCULOS ESTRUCTURALES");
    }, [beamsMatrix, slabsMatrix]); 

    // Función para calcular el área de refuerzo
    const calculateSteelArea = (numBars, diameter) => 
    {      
      const radius = diameter / 20; // Convertir mm a cm y obtener radio
      const areaOneBar = Math.PI * Math.pow(radius, 2); // Área de una barra
      return (Number((areaOneBar * numBars).toFixed(4))); // Área total en cm²
    };
    
    const calculateBeamSectionUltimateMoment = ( b, d, As ) => 
    {
      //b en metros
      //d en metros 
      //As en cm²
      const beta1 = 0.85;   // Para concretos comunes
      const phi = 0.9;      // Factor de reducción de resistencia
      const fc = 210;       // Resistencia del concreto (kg/cm²)
      const fy = 4200;      // Esfuerzo de fluencia del acero (kg/cm²)
    
      // Convertir de metros a centímetros
      const bCm = b * 100;
      const dCm = d * 100;
    
      // 2. Calcular a y c para Mu
      const c = (As * fy) / (0.85 * fc * bCm);
      const a = beta1 * c;
    
      // 3. Calcular el Momento Último
      const Mu = phi * As * fy * (dCm - (a / 2)); // kg·cm
    
      // Convertir de kg·cm a kg·m
      //const MuKgM = Mu / 100;
  
      // Convertir de kg·cm a T·m
      const MuTM = (Mu / 100) / 1000;  
    
      return Number(MuTM.toFixed(2));     
    };

    const calculateBeamUltimateMoment = (longitud, pp) => 
    {
      // Cálculo de la carga distribuida total
      const qu = 1.4 * pp;
    
      // Cálculo del Momento Último Factorado Flexión en el Centro
      const MuCentro = (qu * Math.pow(longitud, 2)) / 8; // MOMENTO EN kg - m    
     
      return Number(MuCentro.toFixed(4));
    };
  
    const startDesign = (beamsMatrix, slabsMatrix, structuralCalculus) => 
    {
     
      const calculatedBeams = structuralCalculus.beamsStructural;
      const column = 0.30 //en metros esto debe venir de las columnas reales
      const padding = 20 //en milímetros, esto es el recubrimiento y se debe poner en las opciones
      const newBeamsMatrix = beamsMatrix;

      calculatedBeams.forEach(beam => 
      {        
        if (!beam.pass)
        {
          let selectedBeam = Object.values(beamsMatrix).find(item => item.id === beam.id && item.position.floor === beam.floor);
          const { geometry } = selectedBeam;
          const { b, h, length } = geometry;
      
          const diameters = [12, 14, 16, 18, 20, 22, 25, 28, 32, 36];
          const minSpacing01 = 2.5; // Espaciamiento mínimo en cm          
          
          let found = false; // Bandera general para saber si ya encontró solución
      
          for (let beamWidth = b; beamWidth <= column; beamWidth += 0.05) 
          {   
            if (found) break; // Si ya encontró, salir
      
            for (let beamHeight = h; beamHeight <= beamWidth * 2.5; beamHeight += 0.05) 
            {  
              if (found) break; // Si ya encontró, salir
      
              for (let dIndex = 0; dIndex < diameters.length; dIndex++) 
              {
                if (found) break; // Si ya encontró, salir
      
                const barDiameter = diameters[dIndex];
                let numBars = 2;
                let greaterThanMuCount = 0; // Reiniciar contador aquí
      
                const pl = beamWidth * beamHeight * 1 * 2400;
                const MuBeamLoads = calculateBeamUltimateMoment(length, pl);
      
                while (numBars <= 5) 
                {
                  const areaSteel = calculateSteelArea(numBars, barDiameter);
                  const d = Number(beamHeight - padding / 1000 - (barDiameter / 2) / 1000).toFixed(4);
                  const MuSection = calculateBeamSectionUltimateMoment(beamWidth, d, areaSteel);
      
                  const minSpacing = Math.max(barDiameter / 1000, minSpacing01 / 100);
                  const calulatedSpacing = Number(
                    (beamWidth - 2 * padding / 1000 - numBars * barDiameter / 1000) / (numBars - 1)
                  ).toFixed(4);
      
                  if (calulatedSpacing <= minSpacing)
                  {
                    break;
                  }
      
                  if (MuSection * 1000 > MuBeamLoads + beam.MuSlabLoads)
                  {
                    greaterThanMuCount++;
                  } else {
                    greaterThanMuCount = 0;
                  }
      
                  if (greaterThanMuCount >= 2)
                  {
                    break;
                  }
      
                  if (
                    MuSection * 1000 > MuBeamLoads + beam.MuSlabLoads &&
                    MuSection * 1000 <= 1.05 * (MuBeamLoads + beam.MuSlabLoads)
                  ) 
                  {
                    const area = beamWidth * beamHeight * 10000; // cm²
                    const volume = parseFloat(Number((area / 10000) * length).toFixed(2));
                    const weight = parseFloat(Number(volume * densityConcrete).toFixed(4)); // Toneladas
      
                    const geometry = 
                    {
                      area: area,
                      volume: volume,
                      weight: weight,
                      b: +(beamWidth).toFixed(2),
                      h: +(beamHeight).toFixed(2),
                      As: areaSteel,
                      barDiameter: barDiameter,
                      numBars: numBars,
                      height : selectedBeam.geometry.height,
                      length : selectedBeam.geometry.length,
                      padding : selectedBeam.geometry.padding,
                    };
      
                    console.log("Viga:", selectedBeam.id, selectedBeam.position.floor);
                    console.log("Mu Calculado:", (MuBeamLoads + beam.MuSlabLoads)/1000);
                    console.log("Mu de la Sección es:", MuSection);
                    console.log("Geometría encontrada:", geometry);

                    const index = newBeamsMatrix.findIndex(b => b.id === selectedBeam.id && b.position.floor === selectedBeam.position.floor);
                    console.log(index);
      
                    //newBeamsMatrix[index].geometry = geometry;
      
                    found = true; // ¡Ya encontró solución!
                    break; // Rompe el while
                  }
      
                  numBars++; // Incrementa varillas
                } // while
      
              } // for diameters
            } // for heights
          } // for widths
      
          if (!found) 
          {
            console.log(`No se encontró solución para la viga ${beam.id}`);
          }
        } 
      });

    
        
      console.log(newBeamsMatrix);

      //setBeamsMatrix(newBeamsMatrix);

      createReactionsMatrix(columnsMatrix, beamsMatrix)
    };  

    return (
      <div className="flex flex-col h-screen">
        
        {/* MENÚ SUPERIOR */}
        <div className="bg-gray-800 text-white flex justify-between items-center px-4 py-2">
          <div className="flex space-x-4">
            
            <div className="relative group">
              <button className="hover:bg-gray-700 px-3 py-1 rounded">Archivo</button>

              <div className="absolute left-0 mt-1 hidden group-hover:block bg-white text-black rounded shadow">
                <button className="block px-4 py-2 hover:bg-gray-100">Nuevo</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Abrir</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Guardar</button>
              </div>
            </div>           

            <button className="hover:bg-gray-700 px-3 py-1 rounded">Más Información</button>

             <div className="relative group">
              <button className="hover:bg-gray-700 px-3 py-1 rounded">Otras Calculadoras</button>
              <div className="absolute left-0 mt-1 hidden group-hover:block bg-white text-black rounded shadow w-[300px]">
                <button className="block px-4 py-2 hover:bg-gray-100">Diseño De Vigas de Hormigón</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Diseño de Losas de Hormigón</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Diseño de Columnas de Hormigón</button>
              </div>
            </div>
            
            <div className="relative group">
              <button className="hover:bg-gray-700 px-3 py-1 rounded">Créditos</button>
              <div className="absolute left-0 mt-1 hidden group-hover:block bg-white text-black rounded shadow w-[300px]">
                <button className="block px-4 py-2 hover:bg-gray-100">Diseño - Javier Hernández</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Pruebas - PMJ Arquitectos</button>
                <button className="block px-4 py-2 hover:bg-gray-100">Revisión - PUCE (Gustavo Leoro)</button>
              </div>
            </div>

            <Link 
              to="/calculadoras"  
              className="hover:bg-gray-700 px-3 py-1 rounded text-white"
            >
                Regresar a Calculadoras
            </Link>  
            

          </div>
          <div>
            <span className="font-bold uppercase">Predimensionamiento de Estructuras de Hormigón</span>
          </div>
        </div>

        {/* AQUÍ VAN LOS COMPONENTES */}
        <div className="flex overflow-hidden h-full">

          {/* Área de Gráficas / Modelo 3D */}
          <div className="flex flex-col bg-gray-50 p-1 flex-1">
            
            <Canvas shadows>
              {pivotPoint.x && pivotPoint.y && pivotPoint.z &&
                <Controls 
                  pivot={pivotPoint}
                /> 
              }

              <PerspectiveCamera 
                makeDefault
                position={[0, 8, 12]}
                fov={75}
              />
              
              <Scene
                xAxis={xAxis}
                zAxis={zAxis}
                floors={floors} 
                intersectionSpacing={intersectionSpacing}
                selectedFloor={selectedFloor}
                pivotPoint =  {pivotPoint}
                gridPoints = {gridPoints}

                spacingMatrix={spacingMatrix}
                heightMatrix = {heightMatrix}

                nodes  = {nodes}

                columnsMatrix = {columnsMatrix}
                beamsMatrix = {beamsMatrix}
                slabsMatrix = {slabsMatrix}

                selectedSpacing = {selectedSpacing}
                setSelectedSpacing = {setSelectedSpacing}
                setSelectedAxesSpacing = {setSelectedAxesSpacing}

                structuralCalculus = {structuralCalculus}
              />
            </Canvas>          

            <SponsortsBar/>
          </div>

          {/* 🔴 Panel Derecho de Inputs  */}
          <div className="bg-white border-l p-4 bg-gray-200 overflow-y-auto z-50 min-w-2/12 w-[300px]">
            <SettingsMenu
              xAxis={xAxis}
              zAxis={zAxis}
              floors={floors} 
              setFloors={setFloors}
              intersectionSpacing={intersectionSpacing}
              setXAxis={setXAxis}
              setZAxis={setZAxis}
              setIntersectionSpacing={setIntersectionSpacing}
              selectedFloor={selectedFloor} 
              setSelectedFloor={setSelectedFloor} 

              spacingMatrix={spacingMatrix} 
              setSpacingMatrix={setSpacingMatrix}
              
              totalVolume = {totalVolume}
              totalWeight = {totalWeight}
              totalArea = {totalArea}

              selectedSpacing = {selectedSpacing}
            />
            
            <button 
              onClick={() => startDesign(beamsMatrix, slabsMatrix, structuralCalculus)} 
              className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-300 active:scale-95"
            >
              Calcular
            </button>
          </div>

          {/* 🔴 Panel Derecho de Botones */}
          <div className="flex flex-col items-center py-2 bg-gray-100 border-l w-16 overflow-y-auto space-y-2 z-50 min-w-1/12 w-[60px]">
            <div className="relative group">
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6 1a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              <div className="absolute right-full mr-1 w-24 bg-gray-700 text-white text-center text-xs rounded py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Configuración
              </div>
            </div>
            <div className="relative group">
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2l9 4.5-9 4.5-9-4.5L12 2zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5z"></path>
                </svg>
              </button>
              <div className="absolute right-full mr-1 w-24 bg-gray-700 text-white text-center text-xs rounded py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Geometría
              </div>
            </div>
            <div className="relative group">
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h18v18H3V3zm3 3h12v12H6V6z"></path>
                </svg>
              </button>
              <div className="absolute right-full mr-1 w-24 bg-gray-700 text-white text-center text-xs rounded py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Cargas
              </div>
            </div>
            <div className="relative group">
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M12 5l7 7-7 7"></path>
                </svg>
              </button>
              <div className="absolute right-full mr-1 w-24 bg-gray-700 text-white text-center text-xs rounded py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Elementos
              </div>
            </div>
          </div>
          
        </div>
      </div>
    );
  };
  
export default Main;